/**
 * /equipment/pressure-control/:equipmentId — one pressure-control component
 * (api#1268, BOP-1). Panels stack full width, never side by side: certificate
 * state, spec, certificate history, service events, and — for a stack
 * assembly — the components in it.
 *
 * APPEND-ONLY. Certificates and service events have no edit or delete on the
 * API; a correction is a new row. So this page offers Record, never Edit, on
 * both lists.
 *
 * THE DOCUMENT goes through the existing upload flow as type `equipment_cert`
 * against this equipment row (presign → PUT → confirm), and its upload id is
 * sent with the certificate. A certificate that the API then refuses would
 * leave the file behind, so the upload is removed again on that path.
 *
 * DATES. A date input's `max` is the API's `meta.today` — the tenant's day —
 * so the browser's own clock never decides what "after today" means. Every
 * figure in the state panel is the API's certState.
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ClipboardPlus, FileText, Loader2, Pencil, Plus, Wrench } from 'lucide-react';
import { api, openUploadDownload } from '../../api/client';
import { putToPresignedUrl } from '../../api/upload';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { PcCertStateSummary } from '../../components/pressure-control/PcCertState';
import { PcSpecDialog } from '../../components/pressure-control/PcSpecDialog';
import { PcComponentTestsPanel, PcExtensionsPanel } from '../../components/pressure-control/PcComponentRecords';
import { useAuth } from '../../contexts/AuthContext';
import { useCanWriteCompanySettings } from '../../hooks/useCanWriteCompanySettings';
import {
  usePcComponent,
  usePcComponents,
  usePcMeta,
  useRecordPcCertification,
  useRecordPcEvent,
  useUpdatePcComponent,
} from '../../hooks/usePressureControl';
import { isNotFound, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import {
  canAdminPc,
  canRecordPcEvent,
  PC_LIST,
  pcClassLabel,
  pcDay,
  pcErrorMessage,
  pcEventLabel,
  pcTriggerLabel,
} from '../../lib/pressure-control';
import { PC_EVENT_KINDS, PC_TRIGGER_REASONS, type PcComponentDetail } from '../../types/pressure-control';
import {
  DH_BAND_SECONDARY,
  DH_DATE_INPUT,
  DH_INPUT,
  DH_SECONDARY,
  DhDialog,
  DhEmpty,
  DhField,
  DhPanel,
  DhSpec,
  useOpenCount,
} from '../downhole/DownholeBits';
import { useBackToList } from '../downhole/recordPage';
import { pcIdentity } from './PressureControlScreen';

const EQUIPMENT_STATUS_LABEL: Record<string, string> = {
  available: 'Available',
  reserved: 'Reserved',
  in_use: 'In use',
  left_on_site: 'Left on site',
  maintenance: 'Maintenance',
  retired: 'Retired',
};

const yes = (v: boolean | null | undefined) => (v === true ? 'Yes' : v === false ? 'No' : '—');
const text = (v: string | null | undefined) => (v && v.trim() ? v : '—');

function ErrorLine({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p role="alert" className="mt-4 rounded-lg border border-fi-signal-red/40 bg-fi-signal-red/10 px-3 py-2 text-sm text-fieldiq-text">
      {message}
    </p>
  );
}

// ─── Record a certificate ────────────────────────────────────────────────────

function RecordCertificateDialog({
  open, onClose, equipmentId, today,
}: { open: boolean; onClose: () => void; equipmentId: string; today: string | null }) {
  const record = useRecordPcCertification(equipmentId);
  const openCount = useOpenCount(open);
  const [certifiedOn, setCertifiedOn] = useState('');
  const [inServiceOn, setInServiceOn] = useState('');
  const [facility, setFacility] = useState('');
  const [engineer, setEngineer] = useState('');
  const [number, setNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setCertifiedOn(''); setInServiceOn(''); setFacility(''); setEngineer('');
    setNumber(''); setNotes(''); setFile(null); setError(null); setBusy(false);
  }, [open, openCount]);

  const submit = async () => {
    setError(null);
    if (!certifiedOn) return setError('Enter the day the certificate was issued.');
    if (inServiceOn && inServiceOn < certifiedOn) return setError('The in-service day cannot be before the certificate day.');
    setBusy(true);
    let uploadId: string | null = null;
    try {
      if (file) {
        const contentType = file.type || 'application/pdf';
        const { data: presigned } = await api.uploads.getPresignedUrl({
          fileName: file.name, contentType, entityType: 'equipment_cert', entityId: equipmentId,
        });
        await putToPresignedUrl(presigned.uploadUrl, file, contentType);
        await api.uploads.confirm({ key: presigned.key, size: file.size });
        uploadId = presigned.uploadId ?? null;
        if (!uploadId) throw new Error('The upload did not return an id.');
      }
    } catch (e: any) {
      setBusy(false);
      return setError(e?.userMessage || pcErrorMessage(e, 'The certificate document could not be uploaded.'));
    }
    try {
      await record.mutateAsync({
        certified_on: certifiedOn,
        in_service_on: inServiceOn || null,
        certifying_facility: facility.trim() || null,
        certifying_engineer: engineer.trim() || null,
        certificate_number: number.trim() || null,
        document_upload_id: uploadId,
        notes: notes.trim() || null,
      });
      setBusy(false);
      onClose();
    } catch (e) {
      // The certificate was refused: take its document back out rather than
      // leave an unattached file on the unit.
      if (uploadId) await api.uploads.delete(uploadId).catch(() => undefined);
      setBusy(false);
      setError(pcErrorMessage(e, 'The certificate could not be recorded.'));
    }
  };

  return (
    <DhDialog
      open={open}
      onClose={onClose}
      title="Record a certificate"
      footer={
        <>
          <button type="button" className={DH_SECONDARY} onClick={onClose}>Cancel</button>
          <button type="button" onClick={submit} disabled={busy} className="fi-action fi-action--standard fi-action--primary">
            {busy && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Record certificate
          </button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <DhField label="Certified on">
          <input type="date" className={DH_DATE_INPUT} value={certifiedOn} max={today ?? undefined} onChange={(e) => setCertifiedOn(e.target.value)} aria-label="Certified on" />
        </DhField>
        <DhField label="In service on" hint="Blank: in storage. The in-service clock starts when it goes into service.">
          <input type="date" className={DH_DATE_INPUT} value={inServiceOn} min={certifiedOn || undefined} max={today ?? undefined} onChange={(e) => setInServiceOn(e.target.value)} aria-label="In service on" />
        </DhField>
        <DhField label="Certifying facility">
          <input className={DH_INPUT} value={facility} maxLength={200} onChange={(e) => setFacility(e.target.value)} />
        </DhField>
        <DhField label="Certifying engineer">
          <input className={DH_INPUT} value={engineer} maxLength={200} onChange={(e) => setEngineer(e.target.value)} />
        </DhField>
        <DhField label="Certificate number">
          <input className={DH_INPUT} value={number} maxLength={100} onChange={(e) => setNumber(e.target.value)} />
        </DhField>
        <DhField label="Certificate document" hint="PDF or photo. Optional.">
          <input type="file" accept="application/pdf,image/*" className="block w-full text-sm text-fieldiq-text" onChange={(e) => setFile(e.target.files?.[0] ?? null)} aria-label="Certificate document" />
        </DhField>
        <div className="sm:col-span-2">
          <DhField label="Notes">
            <textarea className={`${DH_INPUT} h-20 py-2`} value={notes} maxLength={4000} onChange={(e) => setNotes(e.target.value)} />
          </DhField>
        </div>
      </div>
      <p className="mt-3 text-xs text-fieldiq-muted">A certificate cannot be edited once recorded. To correct one, record a new certificate.</p>
      <ErrorLine message={error} />
    </DhDialog>
  );
}

// ─── Record a service event ──────────────────────────────────────────────────

function RecordEventDialog({
  open, onClose, equipmentId, today, nace,
}: { open: boolean; onClose: () => void; equipmentId: string; today: string | null; nace: boolean }) {
  const record = useRecordPcEvent(equipmentId);
  const openCount = useOpenCount(open);
  const [kind, setKind] = useState('');
  const [reason, setReason] = useState('');
  const [occurredOn, setOccurredOn] = useState('');
  const [contextRef, setContextRef] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setKind(''); setReason(''); setOccurredOn(''); setContextRef(''); setNotes(''); setError(null);
  }, [open, openCount]);

  const submit = async () => {
    setError(null);
    if (!kind) return setError('Choose what happened.');
    if (kind === 'recert_trigger' && !reason) return setError('Choose the reason recertification is required.');
    if (!occurredOn) return setError('Enter the day it happened.');
    try {
      await record.mutateAsync({
        event_kind: kind,
        trigger_reason: kind === 'recert_trigger' ? reason : null,
        occurred_on: occurredOn,
        context_ref: contextRef.trim() || null,
        notes: notes.trim() || null,
      });
      onClose();
    } catch (e) {
      setError(pcErrorMessage(e, 'The event could not be recorded.'));
    }
  };

  return (
    <DhDialog
      open={open}
      onClose={onClose}
      title="Record a service event"
      footer={
        <>
          <button type="button" className={DH_SECONDARY} onClick={onClose}>Cancel</button>
          <button type="button" onClick={submit} disabled={record.isPending} className="fi-action fi-action--standard fi-action--primary">
            {record.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Record event
          </button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <DhField label="Event">
          <select className={DH_INPUT} value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Event">
            <option value="">Choose…</option>
            {PC_EVENT_KINDS.map((k) => <option key={k} value={k}>{pcEventLabel(k)}</option>)}
          </select>
        </DhField>
        <DhField label="Occurred on">
          <input type="date" className={DH_DATE_INPUT} value={occurredOn} max={today ?? undefined} onChange={(e) => setOccurredOn(e.target.value)} aria-label="Occurred on" />
        </DhField>
        {kind === 'recert_trigger' && (
          <DhField
            label="Reason"
            hint={nace ? 'This unit is NACE MR0175: a sour exposure does not require recertification.' : undefined}
          >
            <select className={DH_INPUT} value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Reason">
              <option value="">Choose…</option>
              {PC_TRIGGER_REASONS.map((r) => <option key={r} value={r}>{pcTriggerLabel(r)}</option>)}
            </select>
          </DhField>
        )}
        <DhField label="Reference" hint="A job, well or ticket number. Optional.">
          <input className={DH_INPUT} value={contextRef} maxLength={100} onChange={(e) => setContextRef(e.target.value)} />
        </DhField>
        <div className="sm:col-span-2">
          <DhField label="Notes">
            <textarea className={`${DH_INPUT} h-20 py-2`} value={notes} maxLength={4000} onChange={(e) => setNotes(e.target.value)} />
          </DhField>
        </div>
      </div>
      <ErrorLine message={error} />
    </DhDialog>
  );
}

// ─── The page ────────────────────────────────────────────────────────────────

export default function PressureControlDetailScreen() {
  const { equipmentId } = useParams<{ equipmentId: string }>();
  const back = useBackToList(PC_LIST);
  const { user, isFieldWorker } = useAuth();
  const elevated = useCanWriteCompanySettings();
  const canAdmin = canAdminPc(user?.role, elevated);
  const canEvent = canRecordPcEvent(user?.role, elevated);
  // A field hand's route tree has no /equipment/:id (only /field/equipment/:id,
  // which is scoped to the hand's own assigned gear and would 404 a BOP that is
  // not on their job), so the Equipment record button is office-only.
  const showEquipmentLink = !isFieldWorker;
  const [editing, setEditing] = useState(false);
  const [recordingCert, setRecordingCert] = useState(false);
  const [recordingEvent, setRecordingEvent] = useState(false);

  const query = usePcComponent(equipmentId);
  const metaQuery = usePcMeta(canAdmin);
  const listQuery = usePcComponents(canAdmin);
  const update = useUpdatePcComponent(equipmentId ?? '');
  const failure = queryLoadFailure(query);
  const c: PcComponentDetail | null = query.data?.component ?? null;
  const meta = query.data?.meta ?? null;
  const today = meta?.today ?? null;

  const stacks = (listQuery.data?.rows ?? [])
    .filter((r) => r.spec.componentClass === 'stack_assembly')
    .map((r) => ({ equipmentId: r.equipmentId, name: r.name }));

  return (
    <div data-testid="pressure-control-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Pressure control"
        title={c?.name ?? (query.isLoading ? 'Loading…' : 'Component')}
        onBack={back}
        backLabel="Back"
        actions={
          c ? (
            <>
              {showEquipmentLink && (
                <Link to={`/equipment/${c.equipmentId}`} className={DH_BAND_SECONDARY}>
                  <Wrench className="size-4" aria-hidden="true" />
                  Equipment record
                </Link>
              )}
              {canAdmin && (
                <button type="button" onClick={() => setEditing(true)} className={DH_BAND_SECONDARY}>
                  <Pencil className="size-4" aria-hidden="true" />
                  Edit spec
                </button>
              )}
              {canAdmin && (
                <button type="button" onClick={() => setRecordingCert(true)} className="fi-action fi-action--standard fi-action--primary">
                  <Plus className="size-4" aria-hidden="true" />
                  Record certificate
                </button>
              )}
            </>
          ) : undefined
        }
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading component" />
            </div>
          ) : (failure && isNotFound(queryFailureError(query))) || (!failure && !c) ? (
            <DhPanel eyebrow="Pressure control" title="Not found" testId="pc-not-found">
              <DhEmpty>
                This equipment is not a registered pressure-control component, or it has been removed.{' '}
                <Link to={PC_LIST} className="font-semibold underline underline-offset-2">Back to the register</Link>
              </DhEmpty>
            </DhPanel>
          ) : failure ? (
            <DhPanel eyebrow="Pressure control" title="Component" testId="pc-load-failure">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="this component" />
            </DhPanel>
          ) : c ? (
            <>
              <DhPanel eyebrow="Certificate" title="Certificate state" testId="pc-state-panel">
                <div className="space-y-3 px-4 py-4">
                  <PcCertStateSummary state={c.certState} cert={c.currentCertification} />
                  {c.certState?.recertTriggers.length > 0 && (
                    <ul className="space-y-1 text-sm text-fieldiq-text" data-testid="pc-triggers">
                      {c.certState.recertTriggers.map((t, i) => (
                        <li key={`${t.reason}-${t.occurredOn}-${i}`}>
                          {pcTriggerLabel(t.reason)} · {pcDay(t.occurredOn)}
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="text-xs text-fieldiq-muted">
                    {c.certState?.inService ? 'In service' : 'Not in service'}
                    {c.certState?.clockStartedOn ? ` · in-service clock started ${pcDay(c.certState.clockStartedOn)}` : ''}
                    {c.certState?.elastomerRule ? ' · elastomer cap applies' : ''}
                    {' · equipment status '}
                    {EQUIPMENT_STATUS_LABEL[c.equipmentStatus] ?? '—'}
                  </p>
                </div>
              </DhPanel>

              <DhPanel eyebrow="Component" title="Spec" testId="pc-spec-panel">
                <dl className="grid grid-cols-2 gap-4 px-4 py-4 md:grid-cols-4">
                  <DhSpec label="Class" value={pcClassLabel(c.spec.componentClass)} />
                  <DhSpec label="Unit / serial" value={pcIdentity(c)} />
                  <DhSpec label="Manufacturer" value={text(c.manufacturer)} />
                  <DhSpec label="Model" value={text(c.model)} />
                  <DhSpec label="Bore size" value={text(c.spec.boreSize)} />
                  <DhSpec
                    label="Working pressure"
                    mono
                    value={c.spec.workingPressure === null ? '—' : `${c.spec.workingPressure.toLocaleString()} ${c.spec.pressureUnit || ''}`.trim()}
                  />
                  <DhSpec label="Has elastomers" value={yes(c.spec.hasElastomers)} />
                  <DhSpec label="NACE MR0175" value={yes(c.spec.naceMr0175)} />
                  <DhSpec label="Stack" value={text(c.spec.stackParentName)} />
                </dl>
                {c.spec.notes && <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{c.spec.notes}</p>}
              </DhPanel>

              <DhPanel
                eyebrow="Append-only"
                title="Certificates"
                testId="pc-certs-panel"
                footer={c.certifications.length > 0 ? `${c.certifications.length} on file · newest first · a correction is a new certificate` : undefined}
              >
                {c.certifications.length === 0 ? (
                  <DhEmpty>No certificate on file.{canAdmin && ' Record one with Record certificate.'}</DhEmpty>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[760px] text-left">
                      <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                        <tr>
                          <th scope="col" className="px-4 py-3">Certified</th>
                          <th scope="col" className="px-4 py-3">In service</th>
                          <th scope="col" className="px-4 py-3">Number</th>
                          <th scope="col" className="px-4 py-3">Facility / engineer</th>
                          <th scope="col" className="px-4 py-3">Limits</th>
                          <th scope="col" className="px-4 py-3">Document</th>
                        </tr>
                      </thead>
                      <tbody>
                        {c.certifications.map((cert, i) => (
                          <tr key={cert.id} className="border-t border-fieldiq-border" data-testid="pc-cert-row">
                            <td className="px-4 py-3 text-sm text-white">
                              {pcDay(cert.certifiedOn)}
                              {i === 0 && <span className="ml-2 text-xs text-fieldiq-muted">current</span>}
                            </td>
                            <td className="px-4 py-3 text-sm text-fieldiq-text">{pcDay(cert.inServiceOn)}</td>
                            <td className="px-4 py-3 text-sm text-fieldiq-text">{text(cert.certificateNumber)}</td>
                            <td className="px-4 py-3 text-sm text-fieldiq-text">
                              {[cert.certifyingFacility, cert.certifyingEngineer].filter(Boolean).join(' · ') || '—'}
                            </td>
                            <td className="px-4 py-3 text-sm text-fieldiq-text">
                              {Number.isFinite(cert.maxInServiceYears) && Number.isFinite(cert.maxTotalYears)
                                ? `${cert.maxInServiceYears} yr in service · ${cert.maxTotalYears} yr total`
                                : '—'}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {cert.documentUploadId ? (
                                <button
                                  type="button"
                                  onClick={() => void openUploadDownload(cert.documentUploadId!)}
                                  className="inline-flex items-center gap-1 font-semibold text-fieldiq-text underline-offset-2 hover:underline"
                                >
                                  <FileText className="size-4" aria-hidden="true" />
                                  View
                                </button>
                              ) : (
                                <span className="text-fieldiq-muted">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </DhPanel>

              <PcExtensionsPanel component={c} canAdmin={canAdmin} today={today} />

              <PcComponentTestsPanel component={c} canRecord={canEvent} />

              <DhPanel
                eyebrow="Append-only"
                title="Service events"
                testId="pc-events-panel"
                actions={
                  canEvent ? (
                    <button type="button" onClick={() => setRecordingEvent(true)} className={DH_SECONDARY}>
                      <ClipboardPlus className="size-4" aria-hidden="true" />
                      Record event
                    </button>
                  ) : undefined
                }
                footer={c.events.length > 0 ? `${c.events.length} recorded · newest first` : undefined}
              >
                {c.events.length === 0 ? (
                  <DhEmpty>No service events recorded.</DhEmpty>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[640px] text-left">
                      <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                        <tr>
                          <th scope="col" className="px-4 py-3">Occurred</th>
                          <th scope="col" className="px-4 py-3">Event</th>
                          <th scope="col" className="px-4 py-3">Reference</th>
                          <th scope="col" className="px-4 py-3">Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {c.events.map((ev) => (
                          <tr key={ev.id} className="border-t border-fieldiq-border" data-testid="pc-event-row">
                            <td className="px-4 py-3 text-sm text-white">{pcDay(ev.occurredOn)}</td>
                            <td className="px-4 py-3 text-sm text-fieldiq-text">
                              {pcEventLabel(ev.eventKind)}
                              {ev.eventKind === 'recert_trigger' && ` · ${pcTriggerLabel(ev.triggerReason)}`}
                            </td>
                            <td className="px-4 py-3 text-sm text-fieldiq-text">{text(ev.contextRef)}</td>
                            <td className="px-4 py-3 text-sm text-fieldiq-text">{text(ev.notes)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </DhPanel>

              {c.spec.componentClass === 'stack_assembly' && (
                <DhPanel eyebrow="Stack" title="Components in this stack" testId="pc-stack-panel">
                  {c.stackComponents.length === 0 ? (
                    <DhEmpty>No component is assigned to this stack.</DhEmpty>
                  ) : (
                    <ul className="divide-y divide-fieldiq-border">
                      {c.stackComponents.map((k) => (
                        <li key={k.equipmentId}>
                          <Link to={`${PC_LIST}/${k.equipmentId}`} className="flex items-center justify-between px-4 py-3 text-sm hover:bg-fieldiq-dark/50">
                            <span className="font-semibold text-white">{k.name ?? '—'}</span>
                            <span className="text-fieldiq-muted">{pcClassLabel(k.componentClass)}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </DhPanel>
              )}
            </>
          ) : null}
        </div>
      </section>

      {c && canAdmin && (
        <>
          <PcSpecDialog
            open={editing}
            onClose={() => setEditing(false)}
            component={c}
            meta={metaQuery.data}
            pressureUnit={meta?.pressureUnit}
            stacks={stacks}
            pending={update.isPending}
            onSubmit={(body) => update.mutateAsync(body)}
          />
          <RecordCertificateDialog open={recordingCert} onClose={() => setRecordingCert(false)} equipmentId={c.equipmentId} today={today} />
        </>
      )}
      {c && canEvent && (
        <RecordEventDialog
          open={recordingEvent}
          onClose={() => setRecordingEvent(false)}
          equipmentId={c.equipmentId}
          today={today}
          nace={c.spec.naceMr0175}
        />
      )}
    </div>
  );
}
